import type { ReactNode } from "react";
import { Collapse } from "@mui/material";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";

export interface CompletedDrawerBodyProps {
  sections: DrawerSectionMap;
  findingsBlock?: ReactNode | null;
  /**
   * Optional banner rendered at the VERY TOP of the drawer body, ABOVE the
   * Result hero card. Used for the "Ended early — <reason>" notice so an
   * early-exit run states why up front, not below a large Result card.
   */
  topBannerBlock?: ReactNode | null;
  /**
   * Optional non-fatal warning rendered at the very TOP of the completed body,
   * ABOVE the (collapsible) Result section — so the AM sees the caveat even
   * when Result is collapsed. Surfaces post-completion warnings: EPIC writes
   * that landed but couldn't be verified, or a *_warning result key (e.g.
   * service_summary_warning: "issue the Service Summary manually in Epic").
   * Passed undefined when there is no warning.
   */
  verificationWarningBlock?: ReactNode | null;
  /**
   * Optional attribution rendered when this run was finished by an AM's
   * manual clear rather than automatically (email-send-complete-override,
   * AC-2's human-validation half — "the run shows who cleared it, when, and
   * the reason picked"). Same visual band as verificationWarningBlock — ABOVE
   * the Result hero, NOT inside the collapsible Result card, so it stays
   * visible when Result is collapsed. Passed undefined/null for a run that
   * completed the ordinary way.
   */
  manualClearBlock?: ReactNode | null;
  /**
   * Optional extra content rendered immediately after the Result hero card
   * (before Run / Steps / secondary sections). Used by plugin-specific
   * blocks like CarrierHandoff and EndorsementHistory that don't belong in
   * the shared body but need to sit in the right visual position.
   */
  extraTopBlock?: ReactNode | null;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position (ahead of everything else) and,
   * like `phaseStream`, SUPPRESSES the flat steps chip it replaces.
   * Default `undefined` reproduces today's markup exactly — that is what makes
   * the `emuw-step-views` flag-off path a true rollback rather than a second
   * code path.
   */
  stepView?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

export function CompletedDrawerBody({
  sections,
  findingsBlock,
  topBannerBlock,
  extraTopBlock,
  verificationWarningBlock,
  manualClearBlock,
  phaseStream,
  stepView,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: CompletedDrawerBodyProps) {
  return (
    <>
      {/* The phase / milestone main stream, first and outside every
          collapsible. */}
      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {/* Top banner (e.g. "Ended early — <reason>") — ABOVE the hero so it's the
          first thing seen, not buried below the Result card. */}
      {topBannerBlock ?? null}

      {/* Non-fatal post-completion warning — above the Result section, and NOT
          inside a Collapse, so it stays visible even when the AM has the Result
          section collapsed. Passed undefined when clean. */}
      {verificationWarningBlock ?? null}

      {/* Manual-clear attribution (email-send-complete-override) — same band
          as the warning above, NOT inside the Result Collapse, so who/when/
          why stays visible regardless of Result's collapsed state. */}
      {manualClearBlock ?? null}

      {/* AC-7: the Result hero and `extraTopBlock` are CHILDREN of the entry
          point, not primary-body regions — otherwise the four-region
          enumeration fails on the add-in terminal tree. Both are passed into
          MoreInformationSection's extraBlocks below. */}

      {/* The submitted request, in the PRIMARY body — on every
          family, including HITL and terminal (AC-8 / AC-9). */}
      {sections.request ?? null}

      <Collapse in={!!findingsBlock} unmountOnExit timeout={600}>
        {findingsBlock ?? <span />}
      </Collapse>

      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
            {extraTopBlock ?? null}
          </>
        }
      />
    </>
  );
}

export default CompletedDrawerBody;
